import { useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { NavLink as RouterLink, matchPath, useLocation } from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
  Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton,
  Typography, Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 44,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  color: theme.palette.common.white,
  "&:before": {
    top: 0, right: 0, width: 3, bottom: 0, content: "''", display: "none",
    position: "absolute", borderTopLeftRadius: 4, borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.common.white,
  },
}));

const ListItemStyleChild = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 35,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  color: theme.palette.common.white,
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

function NavItem({ item, active }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children, sectionTitle } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightBold",
    bgcolor: alpha(theme.palette.primary.main, 0.15),
    "&:before": { display: "block", backgroundColor: theme.palette.primary.main },
  };

  if (children) {
    return (
      <>
        <ListItemStyle onClick={() => setOpen((p) => !p)} sx={{ ...(isActiveRoot && activeRootStyle) }}>
          <ListItemIconStyle sx={{ color: "common.white" }}>{icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box component={Icon} icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }} />
        </ListItemStyle>
        <Collapse in={open} timeout="auto" sx={{ px: 2, pb: 1 }} unmountOnExit>
          <Grid container direction="column"
            sx={{ backgroundColor: "common.white", borderRadius: "6px" }}>
            {sectionTitle && (
              <Typography sx={{ pl: 2, pt: 1, fontSize: 10, color: "#b7b9cc", textTransform: "uppercase", letterSpacing: 1 }}>
                {sectionTitle}
              </Typography>
            )}
            <List component="div" disablePadding>
              {children.map((child) => {
                const isActiveSub = active(child.path);
                return (
                  <ListItemStyleChild key={child.title} component={RouterLink} to={child.path}>
                    <ListItemText
                      disableTypography
                      sx={{ color: isActiveSub ? "primary.main" : "common.black", fontSize: 13, fontWeight: isActiveSub ? 700 : 400 }}
                      primary={child.title}
                    />
                  </ListItemStyleChild>
                );
              })}
            </List>
          </Grid>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle component={RouterLink} to={path} sx={{ ...(isActiveRoot && activeRootStyle) }}>
      <ListItemIconStyle>{icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} sx={{ fontSize: 14 }} />
      {info && info}
    </ListItemStyle>
  );
}

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  // Group by sectionTitle for top-level dividers
  const sections = [];
  navConfig.forEach((item) => {
    if (item.sectionTitle && (!sections.length || sections[sections.length - 1].label !== item.sectionTitle)) {
      sections.push({ label: item.sectionTitle, items: [item] });
    } else if (sections.length && item.sectionTitle === sections[sections.length - 1].label) {
      sections[sections.length - 1].items.push(item);
    } else if (!item.sectionTitle) {
      // Check if last section has no label
      if (!sections.length || sections[sections.length - 1].label !== null) {
        sections.push({ label: null, items: [item] });
      } else {
        sections[sections.length - 1].items.push(item);
      }
    }
  });

  return (
    <Box {...other}>
      {sections.map((section, i) => (
        <Box key={i}>
          {section.label && (
            <Typography sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 10, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1.5 }}>
              {section.label}
            </Typography>
          )}
          <List disablePadding>
            {section.items.map((item) => (
              <NavItem key={item.title} item={item} active={match} />
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}

NavSection.propTypes = { navConfig: PropTypes.array };
